import { useId, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';

import { CloseIcon } from '../internal/icons';
import { useOverlay } from '../internal/useOverlay';
import { IconButton } from './IconButton';
import styles from './Modal.module.css';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  /** One line under the title. Wired as the dialog's accessible description. */
  description?: ReactNode;
  /** `md` is the mocked-up 880px stage modal. */
  size?: 'sm' | 'md' | 'lg' | 'full';
  /** Action row. Primary action last, as in the mockups. */
  footer?: ReactNode;
  initialFocusRef?: RefObject<HTMLElement | null>;
  closeLabel?: string;
  /** Destructive confirmation: red primary action, stronger title rule. */
  tone?: 'default' | 'danger';
  className?: string;
  children: ReactNode;
}

/**
 * `aria-modal` dialog in a portal: focus moves in, Tab is trapped, Escape
 * closes, and focus returns to the trigger. Clicking the scrim closes too — but
 * only the scrim, so a drag that ends outside the panel does not lose the work.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  size = 'md',
  footer,
  initialFocusRef,
  closeLabel = 'Close',
  tone = 'default',
  className,
  children,
}: ModalProps) {
  const baseId = useId();
  const titleId = `${baseId}-title`;
  const descriptionId = `${baseId}-description`;
  const ref = useOverlay({ open, onClose, trap: true, initialFocus: initialFocusRef });

  if (!open) return null;

  return createPortal(
    <div className={styles.root}>
      <div className={styles.scrim} onClick={onClose} data-testid="modal-scrim" />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description === undefined ? undefined : descriptionId}
        tabIndex={-1}
        className={clsx(styles.dialog, styles[size], tone === 'danger' && styles.danger, className)}
      >
        <header className={styles.header}>
          <div className={styles.headerText}>
            <h2 className={styles.title} id={titleId}>
              {title}
            </h2>
            {description !== undefined && (
              <p className={styles.description} id={descriptionId}>
                {description}
              </p>
            )}
          </div>
          <IconButton label={closeLabel} icon={<CloseIcon />} onClick={onClose} />
        </header>
        <div className={styles.body}>{children}</div>
        {footer !== undefined && <footer className={styles.footer}>{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}
